import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import api from './api';

function GlucoseThresholdManager() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const [formData, setFormData] = useState({
    description: '',
    value: '',
    valueMax: '',
    unit: 'mmol/L',
    type: 'MINIMUM'
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/glucose-threshold-rules');
      if (response.data?.success) {
        setRules(response.data.data);
      }
    } catch (error) {
      setError('Failed to fetch glucose threshold rules');
      console.error('Error fetching rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        description: rule.description,
        value: rule.value.toString(),
        valueMax: rule.valueMax ? rule.valueMax.toString() : '',
        unit: rule.unit,
        type: rule.type || 'MINIMUM'
      });
    } else {
      setEditingRule(null);
      setFormData({
        description: '',
        value: '',
        valueMax: '',
        unit: 'mmol/L',
        type: 'MINIMUM'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRule(null);
    setError('');
    setSuccess('');
  };

  const handleInputChange = (field, value) => {
    console.log(`Field changed: ${field} = ${value}`);
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      console.log('Updated formData:', updated);
      return updated;
    });
  };

  const validateForm = () => {
    if (!formData.description || !formData.description.trim()) {
      setError('Description is required');
      return false;
    }
    if (!formData.value || parseFloat(formData.value) <= 0) {
      setError('Value must be a positive number');
      return false;
    }
    if (!formData.unit || !formData.unit.trim()) {
      setError('Unit is required');
      return false;
    }

    // Validate glucose value ranges based on unit
    const normalizedUnit = formData.unit.toLowerCase().replace(/\s/g, '');
    const value = parseFloat(formData.value);

    if (normalizedUnit === 'mmol/l') {
      if (value < 1.0 || value > 35.0) {
        setError('Glucose value must be between 1.0 and 35.0 mmol/L');
        return false;
      }
    } else if (normalizedUnit === 'mg/dl') {
      if (value < 20 || value > 700) {
        setError('Glucose value must be between 20 and 700 mg/dL');
        return false;
      }
    }

    if (formData.type === 'TARGET_RANGE') {
      if (!formData.valueMax || parseFloat(formData.valueMax) <= 0) {
        setError('Maximum value is required for target range');
        return false;
      }

      const valueMax = parseFloat(formData.valueMax);

      // Validate maximum value range based on unit
      if (normalizedUnit === 'mmol/l') {
        if (valueMax < 1.0 || valueMax > 35.0) {
          setError('Maximum glucose value must be between 1.0 and 35.0 mmol/L');
          return false;
        }
      } else if (normalizedUnit === 'mg/dl') {
        if (valueMax < 20 || valueMax > 700) {
          setError('Maximum glucose value must be between 20 and 700 mg/dL');
          return false;
        }
      }

      if (valueMax <= value) {
        setError('Maximum value must be greater than minimum value');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    const submitData = {
      description: formData.description.trim(),
      value: parseFloat(formData.value),
      unit: formData.unit.trim(),
      type: formData.type
    };

    // Add valueMax only for TARGET_RANGE type
    if (formData.type === 'TARGET_RANGE' && formData.valueMax) {
      submitData.valueMax = parseFloat(formData.valueMax);
    } else {
      submitData.valueMax = null;
    }

    console.log('Submitting threshold rule:', submitData);

    try {
      if (editingRule) {
        const response = await api.put(`/glucose-threshold-rules/admin/${editingRule.id}`, submitData);
        console.log('Update response:', response.data);
      } else {
        const response = await api.post('/glucose-threshold-rules/admin', submitData);
        console.log('Create response:', response.data);
      }
      await fetchRules();

      // Close modal immediately after successful save
      handleCloseDialog();
    } catch (error) {
      setError(error.response?.data?.error?.message || 'Failed to save threshold rule');
      console.error('Error saving threshold rule:', error);
    }
  };

  const handleDelete = async (ruleId) => {
    if (window.confirm('Are you sure you want to delete this threshold rule?')) {
      try {
        await api.delete(`/glucose-threshold-rules/admin/${ruleId}`);
        setSuccess('Threshold rule deleted successfully');
        fetchRules();
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        setError('Failed to delete threshold rule');
        console.error('Error deleting threshold rule:', error);
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Glucose Threshold Rules Management
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Manage glucose level threshold rules for user alerts
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add New Rule
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Value</strong></TableCell>
                  <TableCell><strong>Max Value</strong></TableCell>
                  <TableCell><strong>Unit</strong></TableCell>
                  <TableCell><strong>Created</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No threshold rules found. Click "Add New Rule" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rules.map((rule) => (
                    <TableRow key={rule.id} hover>
                      <TableCell>{rule.description}</TableCell>
                      <TableCell>
                        {rule.type === 'MINIMUM' && 'Minimum'}
                        {rule.type === 'MAXIMUM' && 'Maximum'}
                        {rule.type === 'TARGET_RANGE' && 'Target Range'}
                      </TableCell>
                      <TableCell>{rule.value}</TableCell>
                      <TableCell>{rule.valueMax || '-'}</TableCell>
                      <TableCell>{rule.unit}</TableCell>
                      <TableCell>{new Date(rule.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenDialog(rule)}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(rule.id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRule ? 'Edit Threshold Rule' : 'Add New Threshold Rule'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              margin="normal"
              placeholder="e.g., Critical Low, Low Alert, High Alert"
              helperText="Describe what this threshold represents"
              required
            />

            <FormControl fullWidth margin="normal" required>
              <InputLabel>Threshold Type</InputLabel>
              <Select
                value={formData.type}
                label="Threshold Type"
                onChange={(e) => handleInputChange('type', e.target.value)}
              >
                <MenuItem value="MINIMUM">Minimum (Alert if below)</MenuItem>
                <MenuItem value="MAXIMUM">Maximum (Alert if above)</MenuItem>
                <MenuItem value="TARGET_RANGE">Target Range (Alert if outside range)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={formData.type === 'TARGET_RANGE' ? 'Minimum Value' : 'Value'}
              type="number"
              value={formData.value}
              onChange={(e) => handleInputChange('value', e.target.value)}
              margin="normal"
              inputProps={{ step: "0.1", min: "0" }}
              placeholder="e.g., 3.9, 7.8, 70, 140"
              helperText={formData.type === 'TARGET_RANGE' ? 'Lower bound of the target range' : 'Threshold value'}
              required
            />

            {formData.type === 'TARGET_RANGE' && (
              <TextField
                fullWidth
                label="Maximum Value"
                type="number"
                value={formData.valueMax}
                onChange={(e) => handleInputChange('valueMax', e.target.value)}
                margin="normal"
                inputProps={{ step: "0.1", min: "0" }}
                placeholder="e.g., 10.0, 180"
                helperText="Upper bound of the target range"
                required
              />
            )}

            <FormControl fullWidth margin="normal" required>
              <InputLabel>Unit</InputLabel>
              <Select
                value={formData.unit}
                label="Unit"
                onChange={(e) => handleInputChange('unit', e.target.value)}
              >
                <MenuItem value="mmol/L">mmol/L</MenuItem>
                <MenuItem value="mg/dL">mg/dL</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {editingRule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default GlucoseThresholdManager;
